"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FiSettings } from "react-icons/fi";

import Navbar_Layout1 from "./NavbarLayouts/Layout1";
import Navbar_Layout2 from "./NavbarLayouts/Layout2";
import Navbar_Layout3 from "./NavbarLayouts/Layout3";
import Navbar_Layout4 from "./NavbarLayouts/Layout4";
import Navbar_Layout6 from "./NavbarLayouts/Layout6";
import Navbar_Layout5 from "./NavbarLayouts/Layout5";
import TopBar2 from "./TopBar2";

/* ===================== TYPES ===================== */

interface DropdownLink {
  href: string;
  label: string;
}

interface NavLink {
  id: number;
  url: string;
  newTab: boolean;
  text: string;
  dropDownLinks?: DropdownLink[];
}

interface Button {
  id: number;
  url: string;
  type: string;
  text: string;
  isLive: string;
}

export interface CartResponse {
  id: number;
  attributes: {
    totalAmount: number;
    totalItems: number;
    currency: string;
  };
}

type LayoutType =
  | "Minimal_Edge"
  | "Classic"
  | "Center_Focus"
  | "Floating_Glass"
  | "Double_Deck";

/* ===================== COMPONENT ===================== */

export default function Navbar({
  links,
  logoUrl,
  logoText,
  button,
  layoutBackend,
  cartData,
  topBar2,
}: {
  links: NavLink[];
  logoUrl: string | null;
  logoText: string | null;
  layoutBackend: LayoutType | null;
  button: Button[];
  cartData: CartResponse;
  topBar2: any;
}) {
  const [layout, setLayout] = useState<LayoutType>(
    layoutBackend ?? "Double_Deck",
  );
  const [isHovered, setIsHovered] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  /* ===== Sync with backend ===== */
  useEffect(() => {
    if (layoutBackend) {
      setLayout(layoutBackend);
    }
  }, [layoutBackend]);

  /* ===== Scroll behavior ===== */
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  /* ===== Shared navbar props ===== */
  const navbarProps = {
    links,
    logoUrl,
    logoText,
    button,
    cartData,
    isHovered,
    isScrolled,
    className: `sticky top-0 z-40 w-full transition-all duration-300 ${
      isHovered || isScrolled ? "bg-white shadow-sm" : "bg-transparent"
    }`,
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
  };

  /* ===== Layout resolver ===== */
  const renderLayout = (layout: LayoutType) => {
    switch (layout) {
      case "Minimal_Edge":
        return <Navbar_Layout1 {...(navbarProps as any)} />;

      case "Classic":
        return <Navbar_Layout5 {...(navbarProps as any)} topBar2={topBar2} />;

      case "Center_Focus":
        return <Navbar_Layout2 {...(navbarProps as any)} />;

      case "Floating_Glass":
        return <Navbar_Layout3 {...(navbarProps as any)} />;

      case "Double_Deck":
        return <Navbar_Layout6 {...(navbarProps as any)} topBar2={topBar2} />;

      default:
        return <Navbar_Layout5 {...(navbarProps as any)} />;
    }
  };

  /* ===== Dev layout options ===== */
  const layoutOptions: { value: LayoutType; label: string }[] = [
    { value: "Minimal_Edge", label: "Minimal Edge" },
    { value: "Classic", label: "Classic" },
    { value: "Center_Focus", label: "Center Focus" },
    { value: "Floating_Glass", label: "Floating Glass" },
    { value: "Double_Deck", label: "Double Deck" },
  ];

  /* ===================== RENDER ===================== */

  return (
    <div className="relative">
      {/* Common TopBar */}
      <TopBar2 data={topBar2} />

      {/* Dynamic Navbar */}
      {renderLayout(layout)}

      {/* Dev-only Layout Switcher */}
      {process.env.NODE_ENV === "development" && (
        <div className="fixed top-4 right-4 z-[9999]">
          <button
            onClick={() => setShowDropdown((v) => !v)}
            className="p-2 bg-white border shadow rounded-full hover:bg-gray-100"
            title="Switch Navbar Layout"
          >
            <FiSettings size={20} />
          </button>

          <AnimatePresence>
            {showDropdown && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="mt-2 bg-white border rounded-md shadow-lg p-3 w-56"
              >
                <h4 className="font-semibold mb-2">Navbar Layout</h4>
                <ul className="space-y-1">
                  {layoutOptions.map((option) => (
                    <li key={option.value}>
                      <button
                        className={`w-full text-left px-3 py-1 rounded ${
                          layout === option.value
                            ? "bg-gray-200 font-semibold"
                            : "hover:bg-gray-100"
                        }`}
                        onClick={() => {
                          setLayout(option.value);
                          setShowDropdown(false);
                        }}
                      >
                        {option.label}
                      </button>
                    </li>
                  ))}
                </ul>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
